/**
 * SafeChangeFlow — Parcours guidé "changement sûr" en 3 étapes
 *
 * Étape 1: Sélectionner un nœud + décrire le changement → Analyser l'impact
 * Étape 2: Voir les résultats d'impact → Générer les tests nécessaires
 * Étape 3: Résumé + export/actions
 *
 * Orchestre les APIs backend existantes :
 *   - POST /impact-analyze (impactRadarService)
 *   - POST /generate (test generation)
 *   - GET /node/:name/tests
 *
 * Isolation : composant 100% standalone, contrôlé par feature flag.
 */

import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaShieldAlt, FaArrowRight, FaArrowLeft, FaCheckCircle,
  FaExclamationTriangle, FaSpinner, FaNetworkWired,
  FaClipboardCheck, FaFileExport, FaPlay, FaTimes,
  FaBullseye, FaChartLine
} from 'react-icons/fa';
import apiClient from '../utils/apiClient';
import './SafeChangeFlow.css';

const STEPS = ['select', 'impact', 'summary'];

const SafeChangeFlow = ({ isOpen, onClose, nodes = [], selectedNode: initialNode, user, onSeeTests }) => {
  const { t, i18n } = useTranslation();

  // ─── Wizard state ────────────────────────────────────────────
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Step 1: Select node + describe change
  const [targetNode, setTargetNode] = useState(initialNode || null);
  const [changeDescription, setChangeDescription] = useState('');
  const [changeType, setChangeType] = useState('modification');
  const [nodeSearch, setNodeSearch] = useState('');

  // Step 2: Impact analysis results
  const [impactResult, setImpactResult] = useState(null);

  // Step 3: Generated tests
  const [generatedTests, setGeneratedTests] = useState([]);
  const [testsLoading, setTestsLoading] = useState(false);

  if (!isOpen) return null;

  // ─── Step 1: Analyze Impact ──────────────────────────────────
  const handleAnalyzeImpact = async () => {
    if (!targetNode || !changeDescription.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const res = await apiClient.post('/impact-analyze', {
        change: {
          name: targetNode.name,
          type: changeType,
          description: changeDescription,
          nodeType: targetNode.type,
          technology: targetNode.technology
        },
        lang: i18n.language
      });

      setImpactResult(res.data);
      setCurrentStep(1);

      // Auto-save analysis
      try {
        await apiClient.post('/session/impact-radar', { data: res.data });
      } catch (saveErr) {
        console.warn('SafeChangeFlow: save warning', saveErr);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  // ─── Step 2: Generate Tests ──────────────────────────────────
  const handleGenerateTests = async () => {
    setTestsLoading(true);
    setError(null);

    try {
      // Build schema from target node and its relations
      const relatedNodeNames = (impactResult?.impactedNodes || []).map(n => n.name || n.nodeName);
      const relevantNodes = nodes.filter(n =>
        n.name === targetNode.name || relatedNodeNames.includes(n.name)
      );

      const res = await apiClient.post('/generate-tests', {
        schema: {
          nodes: relevantNodes.map(n => ({
            name: n.name,
            description: n.description,
            type: n.type,
            technology: n.technology,
            status: n.status,
            metadata: n.metadata
          })),
          relationships: [],
          context: {
            changeDescription,
            changeType,
            impactScore: impactResult?.riskScore || 0
          }
        },
        lang: i18n.language
      });

      // /generate-tests returns { test: rawString } where rawString is OpenAI markdown output
      // Extract ALL JSON arrays embedded in the text (OpenAI returns multiple ```json blocks)
      const raw = res.data?.test;
      let tests = [];
      if (Array.isArray(raw)) {
        tests = raw;
      } else if (raw && typeof raw === 'object') {
        tests = raw.scenarios || raw.tests || [];
      } else if (typeof raw === 'string') {
        // Extract all ```json ... ``` code blocks
        const jsonBlocks = raw.match(/```json\s*(\[[\s\S]*?\])\s*```/g) || [];
        for (const block of jsonBlocks) {
          const content = block.replace(/```json\s*|\s*```/g, '');
          try {
            const parsed = JSON.parse(content);
            if (Array.isArray(parsed)) tests = tests.concat(parsed);
          } catch (e) { /* ignore malformed block */ }
        }
        // Fallback: if no code blocks, try extracting first raw array
        if (tests.length === 0) {
          const match = raw.match(/\[\s*\{[\s\S]*?\}\s*\]/);
          if (match) {
            try { tests = JSON.parse(match[0]); } catch (e) { /* ignore */ }
          }
        }
      }
      setGeneratedTests(Array.isArray(tests) ? tests : []);
      setCurrentStep(2);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setTestsLoading(false);
    }
  };

  // ─── Filtered nodes for search ───────────────────────────────
  const filteredNodes = nodes.filter(n =>
    n.name.toLowerCase().includes(nodeSearch.toLowerCase())
  );

  // ─── Helpers ─────────────────────────────────────────────────
  const impactScore = impactResult?.riskScore || 0;
  const impactedNodes = impactResult?.impactedNodes || [];
  const riskLevel = impactScore >= 70 ? 'high' : impactScore >= 40 ? 'medium' : 'low';

  // impactSummary is { i18nKey, i18nParams } — resolve via t() with interpolation params
  const impactSummaryText = impactResult?.impactSummary
    ? (typeof impactResult.impactSummary === 'string'
        ? impactResult.impactSummary
        : t(impactResult.impactSummary.i18nKey, impactResult.impactSummary.i18nParams || {}))
    : null;

  const handleExportSummary = () => {
    const summary = {
      change: {
        node: targetNode?.name,
        type: changeType,
        description: changeDescription
      },
      impact: {
        score: impactScore,
        riskLevel,
        impactedNodesCount: impactedNodes.length,
          impactedNodes: impactedNodes.map(n => n.name)
      },
      tests: generatedTests.map(t => ({
        title: t.title || t.name,
        type: t.testType || t.type,
        priority: t.priority
      })),
      exportedAt: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(summary, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `safe-change-${targetNode?.name}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ─── Render ──────────────────────────────────────────────────
  return (
    <div className="scf-overlay" onClick={onClose}>
      <div className="scf-modal" onClick={e => e.stopPropagation()}>
        {/* ─── Header ─── */}
        <div className="scf-header">
          <div className="scf-header-left">
            <FaShieldAlt className="scf-header-icon" />
            <h2>{t('safeChangeFlow.title')}</h2>
          </div>
          <button className="scf-close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* ─── Progress ─── */}
        <div className="scf-progress">
          {STEPS.map((step, i) => (
            <div key={step} className={`scf-progress-step ${i <= currentStep ? 'scf-step-done' : ''} ${i === currentStep ? 'scf-step-current' : ''}`}>
              <div className="scf-progress-dot">
                {i < currentStep ? <FaCheckCircle /> : <span>{i + 1}</span>}
              </div>
              <span className="scf-progress-label">{t(`safeChangeFlow.steps.${step}`)}</span>
              {i < STEPS.length - 1 && <div className="scf-progress-line" />}
            </div>
          ))}
        </div>

        {/* ─── Error ─── */}
        {error && (
          <div className="scf-error">
            <FaExclamationTriangle /> {error}
          </div>
        )}

        {/* ─── Step 1: Select & Describe ─── */}
        {currentStep === 0 && (
          <div className="scf-step-content">
            <h3>{t('safeChangeFlow.step1.title')}</h3>
            <p className="scf-step-desc">{t('safeChangeFlow.step1.desc')}</p>

            {/* Node selector */}
            <div className="scf-field">
              <label>{t('safeChangeFlow.step1.selectNode')}</label>
              {targetNode ? (
                <div className="scf-selected-node">
                  <FaNetworkWired />
                  <span>{targetNode.name}</span>
                  <span className="scf-node-type">{targetNode.type}</span>
                  <button className="scf-change-node" onClick={() => setTargetNode(null)}>
                    {t('safeChangeFlow.step1.changeNode')}
                  </button>
                </div>
              ) : (
                <div className="scf-node-picker">
                  <input
                    type="text"
                    placeholder={t('safeChangeFlow.step1.searchNode')}
                    value={nodeSearch}
                    onChange={e => setNodeSearch(e.target.value)}
                    className="scf-search-input"
                  />
                  <div className="scf-node-list">
                    {filteredNodes.slice(0, 10).map(node => (
                      <button key={node.name} className="scf-node-item" onClick={() => { setTargetNode(node); setNodeSearch(''); }}>
                        <FaNetworkWired />
                        <span>{node.name}</span>
                        <span className="scf-node-type">{node.type}</span>
                      </button>
                    ))}
                    {filteredNodes.length === 0 && (
                      <p className="scf-no-results">{t('safeChangeFlow.step1.noNodes')}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Change type */}
            <div className="scf-field">
              <label>{t('safeChangeFlow.step1.changeType')}</label>
              <div className="scf-type-options">
                {['modification', 'deletion', 'addition', 'refactoring'].map(type => (
                  <button
                    key={type}
                    className={`scf-type-btn ${changeType === type ? 'scf-type-active' : ''}`}
                    onClick={() => setChangeType(type)}
                  >
                    {t(`safeChangeFlow.step1.types.${type}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Change description */}
            <div className="scf-field">
              <label>{t('safeChangeFlow.step1.describe')}</label>
              <textarea
                className="scf-textarea"
                placeholder={t('safeChangeFlow.step1.describePlaceholder')}
                value={changeDescription}
                onChange={e => setChangeDescription(e.target.value)}
                rows={4}
              />
            </div>

            <div className="scf-actions">
              <button
                className="scf-primary-btn"
                disabled={!targetNode || !changeDescription.trim() || loading}
                onClick={handleAnalyzeImpact}
              >
                {loading ? (
                  <><FaSpinner className="scf-spin" /> {t('safeChangeFlow.step1.analyzing')}</>
                ) : (
                  <><FaShieldAlt /> {t('safeChangeFlow.step1.analyzeBtn')}</>
                )}
              </button>
            </div>
          </div>
        )}

        {/* ─── Step 2: Impact Results ─── */}
        {currentStep === 1 && impactResult && (
          <div className="scf-step-content">
            <h3>{t('safeChangeFlow.step2.title')}</h3>

            {/* Impact score */}
            <div className={`scf-impact-score scf-risk-${riskLevel}`}>
              <div className="scf-score-circle">
                <span className="scf-score-value">{impactScore}</span>
                <span className="scf-score-label">/100</span>
              </div>
              <div className="scf-score-info">
                <h4>{t(`safeChangeFlow.step2.risk.${riskLevel}`)}</h4>
                <p>{t('safeChangeFlow.step2.impactedCount', { count: impactedNodes.length })}</p>
              </div>
            </div>

            {/* Impacted nodes list */}
            {impactedNodes.length > 0 && (
              <div className="scf-impacted-list">
                <h4>{t('safeChangeFlow.step2.impactedNodes')}</h4>
                <div className="scf-impacted-grid">
                  {impactedNodes.slice(0, 8).map((node, i) => (
                    <div key={i} className="scf-impacted-item">
                      <FaNetworkWired />
                      <span>{node.name}</span>
                      <span className="scf-impact-type">{node.impactType || node.type}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* AI recommendation */}
            {impactSummaryText && (
              <div className="scf-recommendation">
                <FaBullseye className="scf-rec-icon" />
                <p>{impactSummaryText}</p>
              </div>
            )}

            <div className="scf-actions">
              <button className="scf-secondary-btn" onClick={() => setCurrentStep(0)}>
                <FaArrowLeft /> {t('safeChangeFlow.back')}
              </button>
              <button
                className="scf-primary-btn"
                disabled={testsLoading}
                onClick={handleGenerateTests}
              >
                {testsLoading ? (
                  <><FaSpinner className="scf-spin" /> {t('safeChangeFlow.step2.generating')}</>
                ) : (
                  <><FaClipboardCheck /> {t('safeChangeFlow.step2.generateBtn')}</>
                )}
              </button>
            </div>
          </div>
        )}

        {/* ─── Step 3: Summary & Export ─── */}
        {currentStep === 2 && (
          <div className="scf-step-content">
            <h3>{t('safeChangeFlow.step3.title')}</h3>

            {/* Summary cards */}
            <div className="scf-summary-grid">
              <div className="scf-summary-card">
                <FaNetworkWired className="scf-summary-icon" />
                <span className="scf-summary-value">{targetNode?.name}</span>
                <span className="scf-summary-label">{t('safeChangeFlow.step3.targetNode')}</span>
              </div>
              <div className={`scf-summary-card scf-risk-${riskLevel}`}>
                <FaChartLine className="scf-summary-icon" />
                <span className="scf-summary-value">{impactScore}/100</span>
                <span className="scf-summary-label">{t('safeChangeFlow.step3.impactScore')}</span>
              </div>
              <div className="scf-summary-card">
                <FaShieldAlt className="scf-summary-icon" />
                <span className="scf-summary-value">{impactedNodes.length}</span>
                <span className="scf-summary-label">{t('safeChangeFlow.step3.nodesImpacted')}</span>
              </div>
              <div className="scf-summary-card">
                <FaClipboardCheck className="scf-summary-icon" />
                <span className="scf-summary-value">{generatedTests.length}</span>
                <span className="scf-summary-label">{t('safeChangeFlow.step3.testsGenerated')}</span>
              </div>
            </div>

            {/* Generated tests preview */}
            {generatedTests.length > 0 && (
              <div className="scf-tests-preview">
                <h4>{t('safeChangeFlow.step3.testsPreview')}</h4>
                {generatedTests.slice(0, 5).map((test, i) => (
                  <div key={i} className="scf-test-item">
                    <FaClipboardCheck className="scf-test-icon" />
                    <div className="scf-test-info">
                      <strong>{test.title || test.name || `Test ${i + 1}`}</strong>
                      <span className="scf-test-type">{test.testType || test.type || 'functional'}</span>
                    </div>
                    {test.priority && (
                      <span className={`scf-test-priority scf-priority-${test.priority}`}>
                        {test.priority}
                      </span>
                    )}
                  </div>
                ))}
                {generatedTests.length > 5 && (
                  <p className="scf-more-tests">
                    +{generatedTests.length - 5} {t('safeChangeFlow.step3.moreTests')}
                  </p>
                )}
              </div>
            )}

            {/* Completion message */}
            <div className="scf-complete-message">
              <FaCheckCircle className="scf-complete-icon" />
              <p>{t('safeChangeFlow.step3.complete')}</p>
            </div>

            <div className="scf-actions">
              <button className="scf-secondary-btn" onClick={() => setCurrentStep(1)}>
                <FaArrowLeft /> {t('safeChangeFlow.back')}
              </button>
              {generatedTests.length > 0 && (
                <button className="scf-secondary-btn" onClick={() => onSeeTests && onSeeTests(targetNode)}>
                  <FaClipboardCheck /> {t('safeChangeFlow.step3.seeTests')}
                </button>
              )}
              <button className="scf-export-btn" onClick={handleExportSummary}>
                <FaFileExport /> {t('safeChangeFlow.step3.exportBtn')}
              </button>
              <button className="scf-primary-btn" onClick={onClose}>
                <FaCheckCircle /> {t('safeChangeFlow.step3.doneBtn')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SafeChangeFlow;
